import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetRiskById } from '@/hooks/queries';
import { useIsModuleEnabled } from '@/hooks/useIsModuleEnabled';
import { toLocalDate } from '@/utils/dateUtils';

import type {
  DetailsOwnerData,
  DetailsSectionData,
  UseRiskOverviewPanelDetailsResult,
} from '../types';

export const useRiskOverviewPanelDetails = (
  riskId: string
): UseRiskOverviewPanelDetailsResult => {
  const { t: detailsT } = useTranslation('common', {
    keyPrefix: 'riskOverviewPanel.details',
  });
  const { data: riskData, loading } = useGetRiskById({ queryArgs: { riskId } });
  const risk = riskData?.risk[0];

  const moduleEnabled = useIsModuleEnabled('enterprise_risk');
  const title = detailsT('title');

  const data = useMemo((): DetailsSectionData => {
    const empty = {
      title,
      entity: undefined,
      status: null,
      lastModifiedByName: null,
      lastModifiedAt: null,
      owners: [],
    };

    if (!risk) {
      return empty;
    }

    const entity = moduleEnabled
      ? { displayName: risk.enterpriseRiskInstance?.entity?.Name ?? null }
      : undefined;

    const userOwners = (risk.owners ?? []).flatMap(
      (owner): DetailsOwnerData[] => {
        if (!owner.user?.Id || !owner.user.FriendlyName) {
          return [];
        }

        return [
          { id: owner.user.Id, name: owner.user.FriendlyName, type: 'user' },
        ];
      }
    );

    const groupOwners = (risk.ownerGroups ?? []).flatMap(
      (ownerGroup): DetailsOwnerData[] => {
        if (!ownerGroup.UserGroupId || !ownerGroup.group?.Name) {
          return [];
        }

        return [
          {
            id: ownerGroup.UserGroupId,
            name: ownerGroup.group.Name,
            type: 'userGroup',
          },
        ];
      }
    );

    const owners: DetailsOwnerData[] = [...userOwners, ...groupOwners];

    return {
      title,
      entity,
      status: risk.Status ?? null,
      lastModifiedByName: risk.modifiedByUser?.FriendlyName ?? null,
      lastModifiedAt: risk.ModifiedAtTimestamp
        ? toLocalDate(risk.ModifiedAtTimestamp)
        : null,
      owners,
    };
  }, [risk, moduleEnabled, title]);

  return { data, loading };
};
